/*
==============================================================================
VELORA DESIGN SYSTEM (VDS)
------------------------------------------------------------------------------
File:        velora-p4.js   (P4 — presentation/progressive enhancement only)
Purpose:     1) Sticky dossier-nav active state (IntersectionObserver).
             2) Hero verification mini-status kept in sync with the selected
                variant via the EXISTING COM-001B #vblCoaData island
                (read-only consumer; COM-001B Rev-6 logic untouched).
             3) Option-A verification enrichment (Authority F1): for a READY
                selected variant only, fetch the existing governed surfaces
                /a/lot/current?variant_id=… (derive published lot from the
                governed redirect) then /a/lot/verify?lot=… and render ONLY
                the values LOT-001 returns. No analytical value exists in
                this file, in Liquid, or in any theme data island.
Fail-closed: any non-success / unresolved lot / non-200 / malformed JSON /
             missing field / timeout / variant change mid-flight leaves the
             accepted status-only experience exactly as server-rendered.
             Every variant change clears enriched state FIRST and aborts any
             outstanding request (AbortController + sequence guard).
==============================================================================
*/
(function () {
  'use strict';

  /* ---------- shared: existing COM-001B data island (read-only) ---------- */
  var dataEl = document.getElementById('vblCoaData');
  var data = null;
  if (dataEl) { try { data = JSON.parse(dataEl.textContent); } catch (e) { data = null; } }

  /* ---------------- 1) dossier nav: sticky + active state ---------------- */
  var nav = document.getElementById('vblDossierNav');
  if (nav && 'IntersectionObserver' in window) {
    var links = {};
    nav.querySelectorAll('a[href^="#"]').forEach(function (a) {
      links[a.getAttribute('href').slice(1)] = a;
    });
    var setActive = function (id) {
      Object.keys(links).forEach(function (k) {
        links[k].classList.toggle('is-active', k === id);
      });
    };
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) setActive(en.target.id);
      });
    }, { rootMargin: '-25% 0px -65% 0px' });
    Object.keys(links).forEach(function (id) {
      var el = document.getElementById(id);
      if (el) io.observe(el);
    });
  }

  /* ------- 2+3) verification: hero sync + Option-A enrichment ------------ */
  if (!data || !data.variants) return;

  var hero      = document.getElementById('vblHeroVerify');
  var heroReady = document.getElementById('vblHeroVerifyReady');
  var heroProg  = document.getElementById('vblHeroVerifyProg');
  var heroLot   = document.getElementById('vblHeroVerifyLot');
  var heroLink  = document.getElementById('vblHeroVerifyLink');
  var lotEl     = document.getElementById('vblCoaLot');
  var metrics   = document.getElementById('vblCoaMetrics');

  var seq = 0;              // sequence guard: stale responses are discarded
  var controller = null;    // AbortController for in-flight requests

  function setTile(name, value) {
    if (!metrics) return false;
    var tile = metrics.querySelector('[data-metric="' + name + '"]');
    if (!tile) return false;
    var v = tile.querySelector('.vbl-coa-metric__v');
    if (value == null || value === '') { tile.hidden = true; if (v) v.textContent = ''; return false; }
    if (v) v.textContent = value;
    tile.hidden = false;
    return true;
  }

  function clearEnriched() {
    if (lotEl) { lotEl.hidden = true; lotEl.textContent = ''; }
    if (heroLot) { heroLot.hidden = true; heroLot.textContent = ''; }
    if (metrics) {
      metrics.hidden = true;
      metrics.querySelectorAll('[data-metric]').forEach(function (t) {
        t.hidden = true;
        var v = t.querySelector('.vbl-coa-metric__v');
        if (v) v.textContent = '';
      });
    }
  }

  function setHero(status, route) {
    if (!hero) return;
    var isReady = status === 'ready';
    var isProg = status === 'in_progress';
    hero.hidden = !(isReady || isProg);
    if (heroReady) heroReady.hidden = !isReady;
    if (heroProg) heroProg.hidden = !isProg;
    if (heroLink) {
      if (isReady && route) { heroLink.setAttribute('href', route); }
      else { heroLink.removeAttribute('href'); }
    }
  }

  function enrich(route, mySeq) {
    if (!('fetch' in window)) return;
    controller = ('AbortController' in window) ? new AbortController() : null;
    var opts = { credentials: 'same-origin', signal: controller ? controller.signal : undefined };
    fetch(route, opts).then(function (r) {
      if (mySeq !== seq || !r.ok) return null;
      var m = (r.url || '').match(/\/a\/lot\/record\/([^/?#]+)/);
      if (!m) return null;                              // fallback/HTML response: stay status-only
      var lot = decodeURIComponent(m[1]);
      return fetch('/a/lot/verify?lot=' + encodeURIComponent(lot), opts).then(function (r2) {
        if (mySeq !== seq || !r2 || !r2.ok) return null;
        return r2.json().then(function (j) {
          if (mySeq !== seq || !j || j.status !== 'ready' || j.lot !== lot) return null;
          // Render ONLY LOT-001-supplied values; omit anything missing/unexpected.
          if (lotEl) { lotEl.textContent = lot + ' — Verified'; lotEl.hidden = false; }
          if (heroLot) { heroLot.textContent = 'Lot ' + lot; heroLot.hidden = false; }
          var shown = false;
          shown = setTile('identity', j.identity === 'confirmed' ? 'Confirmed' : null) || shown;
          var purity = (typeof j.purity_pct === 'number' || (typeof j.purity_pct === 'string' && j.purity_pct !== ''))
            ? parseFloat(j.purity_pct) : NaN;
          shown = setTile('purity', isNaN(purity) ? null : purity.toFixed(2) + '%') || shown;
          shown = setTile('endotoxin', j.endotoxin === 'pass' ? 'PASS' : null) || shown;
          shown = setTile('fentanyl', j.fentanyl === 'not_detected' ? 'Not Detected' : null) || shown;
          if (shown && metrics && mySeq === seq) metrics.hidden = false;
        });
      });
    }).catch(function () { /* fail closed: accepted status-only experience remains */ });
  }

  function update(variantId) {
    seq += 1;
    if (controller) { try { controller.abort(); } catch (e) {} controller = null; }
    clearEnriched();                                   // never show stale metrics
    var v = data.variants[String(variantId)] || {};
    var status = v.status || '';
    setHero(status, v.route || '');
    if (status === 'ready' && v.route) enrich(v.route, seq);
  }

  // Same selected-variant authority/event path as the purchase + COM-001B system.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.id === 'vblVariantSelect') update(t.value);
  });
  var sel = document.getElementById('vblVariantSelect');
  var hiddenInput = document.getElementById('vblVariantId');
  var initial = (sel && sel.value) || (hiddenInput && hiddenInput.value) || '';
  if (initial) update(initial);
})();
